import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAccess } from '../App';
import { validateAccessCode, trackEvent } from '../utils/api';
import { Lock, ArrowRight, Loader2, AlertCircle, Sparkles } from 'lucide-react';

function AccessGate() {
  const { grantAccess, sessionId } = useAccess();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [shake, setShake] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const result = await validateAccessCode(code.trim());
      
      if (result.valid) {
        await trackEvent('code_entry', { code: code.toUpperCase(), success: true }, sessionId, code);
        grantAccess(code.toUpperCase(), result.name);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid access code');
      setShake(true);
      await trackEvent('code_entry', { code: code.toUpperCase(), success: false }, sessionId, null);
      setTimeout(() => setShake(false), 500);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 bg-surface-dark">
        <div className="absolute inset-0 bg-gradient-radial from-kore-900/30 via-transparent to-transparent" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-kore-500/10 rounded-full blur-3xl animate-pulse-slow" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent-lavender/10 rounded-full blur-3xl animate-pulse-slow delay-1000" />
        <div className="absolute inset-0 noise pointer-events-none" />
      </div>

      {/* Content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative z-10 w-full max-w-md px-6"
      >
        <div className="text-center mb-10">
          {/* Logo */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className="inline-flex items-center justify-center w-20 h-20 mb-6 rounded-2xl bg-gradient-to-br from-kore-500 to-kore-700 shadow-2xl shadow-kore-500/30"
          >
            <span className="text-3xl font-bold text-white">K</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-4xl font-bold text-white mb-2"
          >
            KoreFolio
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-zinc-400"
          >
            Investor Pitch Deck
          </motion.p>
        </div>

        {/* Access Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className={`bg-glass rounded-2xl p-8 ${shake ? 'animate-shake' : ''}`}
          style={{
            animation: shake ? 'shake 0.5s cubic-bezier(.36,.07,.19,.97) both' : 'none'
          }}
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg bg-kore-500/10">
              <Lock className="w-5 h-5 text-kore-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Access Required</h2>
              <p className="text-sm text-zinc-500">Enter your access code to continue</p>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="relative mb-4">
              <input
                type="text"
                value={code}
                onChange={(e) => {
                  setCode(e.target.value.toUpperCase());
                  setError(null);
                }}
                placeholder="Enter access code"
                className="input-field text-center text-lg tracking-widest uppercase font-mono"
                autoFocus
                disabled={loading}
              />
              {code && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                >
                  <Sparkles className="w-5 h-5 text-kore-400" />
                </motion.div>
              )}
            </div>

            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="flex items-center gap-2 text-red-400 text-sm mb-4"
                >
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </motion.div>
              )}
            </AnimatePresence>

            <button
              type="submit"
              disabled={loading || !code.trim()}
              className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  Access Deck
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </form>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="text-center text-zinc-500 text-sm mt-6"
        >
          Don't have a code?{' '}
          <a href="mailto:invest@korefolio.com" className="text-kore-400 hover:text-kore-300 transition-colors">
            Request access
          </a>
        </motion.p>
      </motion.div>

      {/* CSS for shake animation */}
      <style>{`
        @keyframes shake {
          10%, 90% { transform: translate3d(-1px, 0, 0); }
          20%, 80% { transform: translate3d(2px, 0, 0); }
          30%, 50%, 70% { transform: translate3d(-4px, 0, 0); }
          40%, 60% { transform: translate3d(4px, 0, 0); }
        }
      `}</style>
    </div>
  );
}

export default AccessGate;
